import { FilterType } from '~/components/filter/filter-options';
import { FieldModel } from '~/models/form-model';
import { FormParameterType } from '~/models/form-parameters';
import { ContributionLinesField } from '~/models/generated/entities/ContributionLines';
import {
	combineModifiedFields,
	FieldNameParamType,
} from '~/utils/acurity-field-utils';
import FormModelUtils from '~/utils/form-model-utils';

export interface SearchColumnDefinition {
	name: string;
	label: string;
	dataType?: {
		name: 'Date' | 'Currency';
	};
	/**
	 * hidden will query the endpoint for the specified column but will
	 * not display it. This is at least used by *_Deleted fields to determine
	 * if we should highlight a row or not.
	 */
	hidden?: boolean;
	/**
	 * Set this to true for columns we want to display in search results
	 * but not add to SQL where clause, eg if a column has no index
	 */
	excludeFromFilter?: boolean;
	render?: (suggestion: any) => string;
}

/**
 * SearchFilter is a complex filtering data structure utilized by the filter component on
 * most classic form pages. It is also utilized by the <a-search> field.
 */
export interface SearchFilter {
	value: string | number;
	operation: FilterType;
	name?: string;
	data?: string;
	not?: boolean;
	andJoin?: boolean;
	ignoreCase?: boolean;
}

export interface FilterBy {
	/**
	 * Col name in current formModel where search field is present.
	 * ie. 'IPz_Transfer_Fund'
	 */
	col: string;
	/**
	 * Col name in entity being searched by this search field.
	 * ie. 'CTz_Fund'
	 */
	targetCol: string;
	/**
	 * Optional property that filters by a field if it has a value
	 * If the value is empty, then remove field from filter map
	 */
	ignoreBlank?: boolean;
}

/**
 * SearchPlainFilter is used by the <a-search> field and can only do equality checking.
 * This aims to be simpler in terms of request parameters sent to the server.
 */
export interface SearchPlainFilter {
	/**
	 * Field or column name of the field value you want to use
	 * from the current form.
	 * ie. "PRz_Name", "MDz_Member"
	 */
	name: string;
	/**
	 * The GET/POST parameter name to be used as the key when
	 * sending to the backend. If no param name is defined, the `name`
	 * will be used.
	 *
	 * ie. "name", "member"
	 */
	paramName?: FormParameterType;
	/**
	 * If set, this value will always be used for the filter.
	 */
	value?: string;
}

/**
 * PreselectedDescription is used by AcuritySearchApi to apply
 * descriptions to preselected fields in ClassicPanelDialog
 */
export interface PreselectedDescription {
	/**
	 * Name of the field onto which the description should be applied, eg PHz_Fund
	 */
	fieldName: string;
	description: string;
}

export enum SearchDefinitionType {
	DATATABLE = 'DATATABLE',
	SEARCH = 'SEARCH',
}

interface SearchDefinitionCommon {
	rowIdKey: string;
	resourcePath: string;
	dropdownWidth: number;
	columnDefinition: SearchColumnDefinition[];
	descriptionDefinition: string[];
}

interface SearchDefinitionDataTable extends SearchDefinitionCommon {
	searchType: SearchDefinitionType.DATATABLE;
	filters?: SearchFilter[];
}

interface SearchDefinitionSearch extends SearchDefinitionCommon {
	searchType: SearchDefinitionType.SEARCH;
	filters?: SearchPlainFilter[];
}

export enum SearchType {
	// ACCOUNT = 'ACCOUNT',
	ADVISER = 'ADVISER',
	MASTER_ADVISER = 'MASTER_ADVISER',
	ADVISER_AUTHORITY_ADVISER = 'ADVISER_AUTHORITY_ADVISER',
	MEMBER_ADVISER_PROPORTIONS = 'MEMBER_ADVISER_PROPORTIONS',
	EMPLOYER_ADVISER_PROPORTIONS = 'EMPLOYER_ADVISER_PROPORTIONS',
	MEMBER_CORRESPONDENCE_REGISTER = 'MEMBER_CORRESPONDENCE_REGISTER',
	PAYROLL_CORRESPONDENCE_REGISTER = 'PAYROLL_CORRESPONDENCE_REGISTER',
	ADVISER_CORRESPONDENCE_REGISTER = 'ADVISER_CORRESPONDENCE_REGISTER',
	BANK_ACCOUNT = 'BANK_ACCOUNT',
	GLOBAL_BANK_ACCOUNT = 'GLOBAL_BANK_ACCOUNT',
	BANK_RECONCILIATION_ACCOUNT = 'BANK_RECONCILIATION_ACCOUNT',
	BATCH_CODES = 'BATCH_CODES',
	BATCH_MERGER = 'BATCH_MERGER',
	BSB = 'BSB_CODES',
	BSB_NO = 'BSB_CODES', // different fields can map to same search definition
	BSB_PAYMENTS = 'BSB_CODES',
	CATEGORY = 'CATEGORY',
	CLIENT = 'CLIENT',
	// CODE = 'CODE',
	// CODES = 'CODES',
	DELEGATE_OR_ORGANISER = 'DELEGATE_OR_ORGANISER',
	DELEGATE = 'DELEGATE',
	ORGANISER = 'ORGANISER',
	FEE_DEFINITIONS = 'FEE_DEFINITIONS',
	FEE_DEFINITIONS_WITH_DELETED = 'FEE_DEFINITIONS_WITH_DELETED',
	AD_HOC_FEE_DEFINITIONS = 'AD_HOC_FEE_DEFINITIONS',
	NON_AD_HOC_FEE_DEFINITIONS = 'NON_AD_HOC_FEE_DEFINITIONS',
	FUND = 'FUND',
	INSURER = 'INSURER',
	// MANAGER = 'MANAGER',
	MEMBER = 'MEMBER',
	MEMBER_ACTIVE_ONLY = 'MEMBER_ACTIVE_ONLY',
	MESSAGE = 'MESSAGE',
	MESSAGE_FOR_MEMBER = 'MESSAGE_FOR_MEMBER',
	MESSAGE_FOR_CLIENT = 'MESSAGE_FOR_CLIENT',
	CONVERSATION_ID = 'CONVERSATION_ID',
	// MGR = 'MGR',
	// NUMBER = 'NUMBER',
	PAYEE = 'PAYEE',
	PAYROLL = 'PAYROLL',
	PAYROLL_LOCATION = 'PAYROLL_LOCATION',
	RECEIPT = 'RECEIPT',
	SCALE_TABLES = 'SCALE_TABLES',
	// TABLES = 'TABLES',
	TRANSN_REF_NUMBER = 'TRANSN_REF_NUMBER',
	TRANSN_REVERSE_ADJUSTMENT_REF_NUMBER = 'TRANSN_REVERSE_ADJUSTMENT_REF_NUMBER',
	INVESTMENT_MANAGER = 'INVESTMENT_MANAGER',
	INVESTMENT_MANAGER_LONG = 'INVESTMENT_MANAGER_LONG',
	POLICY = 'POLICY',
	REPORT = 'REPORT',
	QAS = 'QAS',
	GROUP_INVESTMENT_PROFILE = 'GROUP_INVESTMENT_PROFILE',
	FILE_REGISTER = 'FILE_REGISTER',
	GROUP_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'GROUP_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	EMPLOYER_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'EMPLOYER_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	ADVISER_MODEL_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'ADVISER_MODEL_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	CATEGORY_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'CATEGORY_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	FUND_MODEL_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'FUND_MODEL_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	FUND_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'FUND_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	CONT_TAX_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'CONT_TAX_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	PENSION_REDEMPTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'PENSION_REDEMPTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	CONTRIBUTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'CONTRIBUTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	REDEMPTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'REDEMPTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	USER_DEFINED_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'USER_DEFINED_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	MEMBER_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'MEMBER_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	MEMBER_INVESTMENT_PROFILE_2_INVESTMENT_MANAGER = 'MEMBER_INVESTMENT_PROFILE_2_INVESTMENT_MANAGER',
	NON_MODEL_INVESTMENT_MANAGER = 'NON_MODEL_INVESTMENT_MANAGER',
	MODEL_MANAGER = 'MODEL_MANAGER',
	ROLLOVER_REQUEST_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'ROLLOVER_REQUEST_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	TRANSFER_INVESTMENT_PROFILE_INVESTMENT_MANAGER = 'TRANSFER_INVESTMENT_PROFILE_INVESTMENT_MANAGER',
	TABLE_DEFINITIONS = 'TABLE_DEFINITIONS',
	BANK_ACCOUNT_PAYMENT_PROPORTIONS_BANK_ACCOUNT = 'BANK_ACCOUNT_PAYMENT_PROPORTIONS_BANK_ACCOUNT',
	USER = 'USER',
	RECORD_AUTHORISATION_TABLE_NAME = 'RECORD_AUTHORISATION_TABLE_NAME',
	CONTRIBUTION_LINES = 'CONTRIBUTION_LINES',
	STATUS = 'STATUS',
	TREATY_NUMBER = 'TREATY_NUMBER',
	MESSAGE_CODE = 'MESSAGE_CODE',
	BANK_STMT_REF = 'BANK_STMT_REF',
	ORIG_TRANS_REF = 'ORIG_TRANS_REF',
	MESSAGE_CODE_SUPER_STREAM = 'MESSAGE_CODE_SUPER_STREAM',
	BENEFITS_PAYMENTS_DISPERSALS_CONVERSATION_ID = 'BENEFITS_PAYMENTS_DISPERSALS_CONVERSATION_ID',
	CORPORATE_ACTION_ID = 'CORPORATE_ACTION_ID',
	SUPPRESSION_ISSUES = 'SUPPRESSION_ISSUES',
}

export type SearchDefinition =
	| SearchDefinitionDataTable
	| SearchDefinitionSearch;

const Member: SearchDefinition = {
	searchType: SearchDefinitionType.DATATABLE,
	rowIdKey: 'MDz_Member',
	resourcePath: 'member-details',
	dropdownWidth: 400,
	columnDefinition: [
		{ name: 'MDz_Member', label: 'Member ID' },
		{ name: 'MDz_Fund', label: 'Fund Code' },
		{ name: 'MDz_Surname', label: 'Surname' },
		{ name: 'MDz_Given_Names', label: 'Given Names' },
		{ name: 'MDc_Account_Status', label: 'Status', hidden: true },
	],
	// NOTE (York): WEBUI-3208
	// Member description is from
	// au.com.finsyn.acuritywebserver.core.forms.generic.FieldDescriptionProviderImpl#getFieldDescriptionForMember
	// Surname first, given name follows.
	descriptionDefinition: ['MDz_Surname', 'MDz_Given_Names'],
};

// NOTE: Jake: 2019-10-30 - WEBUI-2639
// Not 100% certain yet. But any field with its type defined as:
// AcurityFieldTypes.Inv_Mgr. Seems to map to this.
const InvestmentManager: SearchDefinition = {
	searchType: SearchDefinitionType.SEARCH,
	rowIdKey: 'IMz_Code',
	resourcePath: 'investment-manager-details',
	dropdownWidth: 400,
	columnDefinition: [
		{ name: 'IMz_Code', label: 'Investment Manager' },
		{ name: 'IMz_Name', label: 'Business Name' },
	],
	descriptionDefinition: ['IMz_Name_Short_Desc'],
	filters: [
		{ name: 'THz_Fund', paramName: 'fund' }, // transaction-79 uses this.
		{ name: 'THz_Member', paramName: 'member' }, // transaction-79 uses this
		// The above two lines are optional because the field names match the parameter names.
		// If the field names did not match the parameters in AcurityFieldUtils.defaultFieldTypes, then they are needed.
		// If they are removed they will be deduced when required. Left here just for illustrative purposes.
		{ name: 'IMz_Status', value: 'IF' }, // The manager must be In-Force
	],
};

// In transaction 61, investment-manager list should not contain a model manager(IMz_Type='M')
const NonModelInvestmentManager: SearchDefinition = {
	searchType: SearchDefinitionType.SEARCH,
	rowIdKey: 'IMz_Code',
	resourcePath: 'investment-manager-details',
	dropdownWidth: 400,
	columnDefinition: [
		{ name: 'IMz_Code', label: 'Investment Manager' },
		{ name: 'IMz_Name', label: 'Business Name' },
		{ name: 'IMz_Type', label: 'Type' },
	],
	descriptionDefinition: ['IMz_Name_Short_Desc'],
	filters: [
		{ name: 'THz_Fund', paramName: 'fund' }, // transaction-79 uses this.
		{ name: 'THz_Member', paramName: 'member' }, // transaction-79 uses this
		{ name: 'IMz_Status', value: 'IF' },
		{ name: 'IMz_Class', value: 'I' },
		{ name: 'IMz_Type', value: '<>M' },
	],
};
const ModelInvestmentManager: SearchDefinition = {
	searchType: SearchDefinitionType.SEARCH,
	rowIdKey: 'IMz_Code',
	resourcePath: 'investment-manager-details',
	dropdownWidth: 400,
	columnDefinition: [
		{ name: 'IMz_Code', label: 'Investment Manager' },
		{ name: 'IMz_Name', label: 'Business Name' },
		{ name: 'IMz_Type', label: 'Type' },
	],
	descriptionDefinition: ['IMz_Name_Short_Desc'],
	filters: [
		{ name: 'IMz_Status', value: 'IF' },
		{ name: 'IMz_Class', value: 'I' },
		{ name: 'IMz_Type', value: 'M' },
	],
};

const InvestmentProfileInvestmentManager: SearchDefinition = {
	searchType: SearchDefinitionType.SEARCH,
	rowIdKey: 'IMz_Code',
	resourcePath: 'investment-manager-details',
	dropdownWidth: 400,
	columnDefinition: [
		{ name: 'IMz_Code', label: 'Code' },
		{ name: 'IMz_Name', label: 'Description' },
	],
	descriptionDefinition: ['IMz_Name_Short_Desc'],
	filters: [
		{ name: 'IPz_Fund' },
		{ name: 'IPz_Member_No' },
		{ name: 'IPz_Client' },
		{ name: 'IPd_Effective' },
		{ name: 'IPz_Transfer_Fund' },
		{ name: 'IPz_Transfer_Cat' },
	],
};

const TransactionRef: SearchDefinition = {
	searchType: SearchDefinitionType.DATATABLE,
	rowIdKey: 'THl_Trans_Ref',
	resourcePath: 'transactions',
	dropdownWidth: 1200,
	columnDefinition: [
		{ name: 'THz_Division', label: 'Division' },
		{ name: 'THz_Fund', label: 'Fund' },
		{ name: 'THl_Trans_Ref', label: 'Trans Reference' },
		{ name: 'THz_Transaction', label: 'Transaction' },
		{ name: 'THz_Payroll', label: 'Employer' },
		{ name: 'THz_Member', label: 'Member' },
		{ name: 'THd_Effective', label: 'Effective Date' },
		{ name: 'THc_Exit', label: 'Exit' },
		{ name: 'THz_Cheque_No', label: 'Cheque Number' },
		{ name: 'THf_Cheque_Amt', label: 'Cheque Amount' },
		{ name: 'THz_Posted', label: 'Posted' },
		{ name: 'THd_Interest_Calc', label: 'Interest Calculation' },
		// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
	],
	descriptionDefinition: ['THz_Posted'],
};

const AdviserProportions: SearchDefinition = {
	searchType: SearchDefinitionType.SEARCH,
	rowIdKey: 'BDz_Name',
	resourcePath: '', // set in MEMBER/EMPLOYER_ADVISER_PROPORTIONS
	dropdownWidth: 400,
	columnDefinition: [
		{ name: 'BDz_Name', label: 'Adviser' },
		{ name: 'BDz_C_Given_Names', label: 'Given Names' },
		{ name: 'BDz_C_Surname', label: 'Surname' },
		{ name: 'BDc_Deleted', label: 'Deleted', hidden: true },
	],
	descriptionDefinition: ['BDz_C_Given_Names', 'BDz_C_Surname'],
	filters: [
		{ name: 'APz_Fund' },
		{ name: 'APz_Member' },
		{ name: 'APz_Payroll' },
		{ name: 'APd_Effective' },
	],
};

const CorrespondenceRegister: SearchDefinition = {
	searchType: SearchDefinitionType.SEARCH,
	rowIdKey: 'DUz_Code',
	resourcePath: '',
	dropdownWidth: 400,
	columnDefinition: [
		{ name: 'DUz_Code', label: 'Code' },
		{ name: 'DUz_Description', label: 'Description' },
	],
	descriptionDefinition: ['DUz_Description'],
	filters: [{ name: 'RGz_Type' }],
};

const Message: SearchDefinition = {
	searchType: SearchDefinitionType.SEARCH,
	rowIdKey: 'MSl_Record_No',
	resourcePath: 'message-history',
	dropdownWidth: 1200,
	columnDefinition: [
		{ name: 'MSl_Record_No', label: 'Record No' },
		{ name: 'MSz_Message_In_Out', label: 'Message Received or Sent' },
		{ name: 'MSz_Message_Type', label: 'Message Type' },
		{ name: 'MSz_ConversationID', label: 'Conversation ID' },
		{ name: 'MSz_PartID', label: 'Superstream PartID' },
		{ name: 'MSz_Status', label: 'Message Status' },
		{ name: 'MSs_Sequence', label: 'Sequence Number' },
		{
			name: 'MSk_Created',
			label: 'Creation Date',
			dataType: { name: 'Date' },
		},
		{ name: 'MSk_Sent', label: 'Sent Date', dataType: { name: 'Date' } },
		{ name: 'MSc_Deleted', label: 'Deleted', hidden: true },
	],
	descriptionDefinition: ['MSz_ConversationID'],
	filters: [{ name: 'fund' }, { name: 'member' }],
};

const MessageCode: SearchDefinition = {
	searchType: SearchDefinitionType.SEARCH,
	rowIdKey: 'ECl_Message_Code',
	resourcePath: 'message-code',
	dropdownWidth: 600,
	columnDefinition: [
		{ name: 'ECz_Process_Type', label: 'Process Type' },
		{ name: 'ECz_Message_Type', label: 'Message Type' },
		{ name: 'ECl_Message_Code', label: 'Message Code' },
		{ name: 'ECz_Description', label: 'Description' },
		{ name: 'ECz_Macro', label: 'Message Macro' },
	],
	descriptionDefinition: [],
};

export const SearchDefinitionMap: { [key in SearchType]: SearchDefinition } = {
	DELEGATE: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'CLz_Location',
		resourcePath: 'organiser-or-delegate/delegate',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'CLz_Fund', label: 'Fund Code' },
			{ name: 'CLz_Location', label: 'Location' },
			{ name: 'CLc_Deleted', label: 'Deleted' },
		],
		descriptionDefinition: ['CLz_Location_Name'],
	},
	FEE_DEFINITIONS: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'FDz_Code',
		resourcePath: 'fee-definitions',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'FDz_Code', label: 'Code' },
			{ name: 'FDz_Description', label: 'Description' },
			{ name: 'FDz_Class', label: 'Class' },
			{ name: 'FDz_Level', label: 'Level' },
			{ name: 'FDz_Offset_Tax', label: 'Offset Tax' },
			{ name: 'FDz_Mbr_Cont_Acct', label: 'Member Contribution Account' },
			{
				name: 'FDz_Mbr_Cont_Pres',
				label: 'Member Contribution Preserved',
			},
		],
		descriptionDefinition: ['FDz_Description'],
		filters: [
			{
				name: 'FDz_Class',
				value: 'C',
				operation: FilterType.EQUALS,
				andJoin: true,
			},
			{
				name: 'FDz_Deleted',
				value: 'Y',
				operation: FilterType.NOT_EQUALS,
				andJoin: true,
			},
		],
	},
	AD_HOC_FEE_DEFINITIONS: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'FDz_Code',
		resourcePath: 'fee-definitions',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'FDz_Code', label: 'Code' },
			{ name: 'FDz_Description', label: 'Description' },
			{ name: 'FDz_Class', label: 'Class' },
			{ name: 'FDz_Level', label: 'Level' },
			{ name: 'FDz_Offset_Tax', label: 'Offset Tax' },
			{ name: 'FDz_Mbr_Cont_Acct', label: 'Member Contribution Account' },
			{
				name: 'FDz_Mbr_Cont_Pres',
				label: 'Member Contribution Preserved',
			},
		],
		descriptionDefinition: ['FDz_Description'],
		filters: [
			{
				name: 'FDz_Event',
				value: 'Y',
				operation: FilterType.EQUALS,
				andJoin: true,
			},
			{
				name: 'FDz_Deleted',
				value: 'Y',
				operation: FilterType.NOT_EQUALS,
				andJoin: true,
			},
		],
	},
	NON_AD_HOC_FEE_DEFINITIONS: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'FDz_Code',
		resourcePath: 'fee-definitions',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'FDz_Code', label: 'Code' },
			{ name: 'FDz_Description', label: 'Description' },
			{ name: 'FDz_Class', label: 'Class' },
			{ name: 'FDz_Level', label: 'Level' },
			{ name: 'FDz_Offset_Tax', label: 'Offset Tax' },
			{ name: 'FDz_Mbr_Cont_Acct', label: 'Member Contribution Account' },
			{
				name: 'FDz_Mbr_Cont_Pres',
				label: 'Member Contribution Preserved',
			},
		],
		descriptionDefinition: ['FDz_Description'],
		filters: [
			{
				name: 'FDz_Event',
				value: 'Y',
				operation: FilterType.EQUALS,
				andJoin: true,
				not: true,
			},
			{
				name: 'FDz_Deleted',
				value: 'Y',
				operation: FilterType.NOT_EQUALS,
				andJoin: true,
			},
		],
	},
	FEE_DEFINITIONS_WITH_DELETED: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'FDz_Code',
		resourcePath: 'fee-definitions',
		dropdownWidth: 600,
		columnDefinition: [
			{ name: 'FDz_Code', label: 'Code' },
			{ name: 'FDz_Description', label: 'Description' },
			{ name: 'FDz_Class', label: 'Class' },
			{ name: 'FDz_Level', label: 'Level' },
			{ name: 'FDz_Offset_Tax', label: 'Offset Tax' },
			{ name: 'FDz_Mbr_Cont_Acct', label: 'Member Contribution Account' },
			{
				name: 'FDz_Mbr_Cont_Pres',
				label: 'Member Contribution Preserved',
			},
		],
		descriptionDefinition: ['FDz_Description'],
	},
	FUND: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'FRz_Fund',
		resourcePath: 'fund-information',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'FRz_Fund', label: 'Fund Code' },
			{ name: 'FRz_Fund_Name', label: 'Fund Name' },
			{ name: 'FRz_BSB_No', label: 'BSB' },
		],
		descriptionDefinition: ['FRz_Fund_Name'],
	},
	ORGANISER: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'CLz_Location',
		resourcePath: 'organiser-or-delegate/organiser',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'CLz_Fund', label: 'Fund Code' },
			{ name: 'CLz_Location', label: 'Location' },
			{ name: 'CLc_Deleted', label: 'Deleted' },
		],
		descriptionDefinition: ['CLz_Location_Name'],
	},
	DELEGATE_OR_ORGANISER: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'CLz_Location',
		resourcePath: 'organiser-or-delegate',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'CLz_Fund', label: 'Fund Code' },
			{ name: 'CLz_Location', label: 'Location' },
			{ name: 'CLc_Deleted', label: 'Deleted' },
		],
		descriptionDefinition: ['CLz_Location_Name'],
	},
	MEMBER: {
		...Member,
	},
	MEMBER_ACTIVE_ONLY: {
		...Member,
		filters: [
			// NOTE: Jae: 2020-01-28 - WEBUI-2945
			// We use 'C' because there's logic in "client-summary.ts" where we compute
			// active accounts by checking if MDc_Account_Status is "C".
			// So if this ends up being incorrect, our client-summary page might be incorrect too.
			{
				name: FormModelUtils.MemberAccountStatusField,
				value: 'C',
				operation: FilterType.EQUALS,
				andJoin: true,
			},
		],
	},
	PAYROLL: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'COz_Payroll',
		resourcePath: 'payroll-details',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'COz_Payroll', label: 'Employer ID' },
			{ name: 'COz_Fund', label: 'Fund ID' },
			{ name: 'COz_Payroll_Name', label: 'Employer Name' },
			{ name: 'COz_Aust_Company_No', label: 'ABN' },
		],
		descriptionDefinition: ['COz_Payroll_Name'],
	},
	GROUP_INVESTMENT_PROFILE: {
		// NOTE(Jae): 2020-02-14 - WEBUI-3039
		// This search endpoint was implemented for the MDz_Group_Profile on
		// "member-and-client-details.ts"
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'IPz_Member_No',
		resourcePath: 'group-investment-profile',
		dropdownWidth: 600,
		columnDefinition: [
			{ name: 'IPz_Fund', label: 'Fund' },
			{ name: 'IPz_Member_No', label: 'Member' },
			{
				name: 'IPd_Effective',
				label: 'Effective Date',
				dataType: { name: 'Date' },
			},
		],
		descriptionDefinition: ['IPz_Member_No'],
	},
	PAYROLL_LOCATION: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'CLz_Location',
		resourcePath: 'payroll-location',
		dropdownWidth: 600,
		columnDefinition: [
			{ name: 'CLz_Fund', label: 'Fund' },
			{ name: 'CLz_Payroll', label: 'Employer' },
			{ name: 'CLz_Location', label: 'Location' },
			{ name: 'CLs_Contact_No', label: 'Contact No' },
			{ name: 'CLz_Contact_Type', label: 'Contact Type' },
			{ name: 'CLz_Title', label: 'Title' },
			{ name: 'CLz_Surname', label: 'Surname' },
			{ name: 'CLz_Given_Names', label: 'Given Name' },
			{ name: 'CLc_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['CLz_Location'],
	},
	CATEGORY: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'CTz_Category',
		resourcePath: 'category',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'CTz_Fund', label: 'Fund' },
			{ name: 'CTz_Category', label: 'Category' },
			{
				name: 'CTd_Effective',
				label: 'Effective Date',
				dataType: { name: 'Date' },
			},
			{ name: 'CTz_Description', label: 'Description' },
			{ name: 'CTz_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['CTz_Description'],
	},
	SCALE_TABLES: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'TZz_Code',
		resourcePath: 'scale-tables',
		dropdownWidth: 600,
		columnDefinition: [
			{ name: 'TZz_Code', label: 'Table' },
			{
				name: 'TZd_Effective',
				label: 'Effective Date',
				dataType: { name: 'Date' },
			},
			{ name: 'TZz_Description', label: 'Description' },
			{ name: 'TZz_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['TZz_Description'],
	},
	BATCH_CODES: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'BTz_Code',
		resourcePath: 'batch-codes',
		dropdownWidth: 300,
		columnDefinition: [
			{ name: 'BTz_Code', label: 'Code' },
			{ name: 'BTz_Description', label: 'Description' },
			{ name: 'BTc_Processed', label: 'Processed' },
			{ name: 'BTc_Deleted', label: 'Deleted', hidden: true },
		],
		filters: [
			{
				name: 'BTz_Batch_Group',
				value: '',
				operation: FilterType.EQUALS,
			}, // this is placeholder for filter-by
		],
		descriptionDefinition: ['BTz_Description'],
	},
	BATCH_MERGER: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'BSl_Receipt_Ref',
		resourcePath: 'receipts/merge-with',
		dropdownWidth: 300,
		columnDefinition: [
			{ name: 'BSl_Receipt_Ref', label: 'Receipt Reference' },
			{ name: 'BSz_Fund', label: 'Fund', excludeFromFilter: true },
			{ name: 'BSz_Member', label: 'Member', excludeFromFilter: true },
			{ name: 'BSz_Payroll', label: 'Employer', excludeFromFilter: true },
			// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
		],
		descriptionDefinition: ['BSz_Member'],
	},
	RECEIPT: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'BSl_Receipt_Ref',
		resourcePath: 'receipts',
		dropdownWidth: 300,
		columnDefinition: [
			{ name: 'BSl_Receipt_Ref', label: 'Receipt Ref' },
			{ name: 'BSz_Fund', label: 'Fund' },
			{ name: 'BSz_Member', label: 'Member' },
			{ name: 'BSz_Payroll', label: 'Employer' }, // TODO: Remove?
		],
		descriptionDefinition: ['BSz_Member'],
		filters: [
			{
				name: 'BSs_Cancel_Batch',
				operation: FilterType.NOT_EQUALS,
				value: '2',
			}, // Batch not Actually Cancelled
			{
				name: 'BSc_Processed',
				operation: FilterType.NOT_EQUALS,
				value: 'Q',
			}, // not Quick Quote
			{
				name: 'BSc_Electronic_Batch',
				operation: FilterType.EQUALS,
				value: 'Y',
			},
		],
	},
	FILE_REGISTER: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'RAz_Filename',
		resourcePath: 'file-register',
		dropdownWidth: 500,
		columnDefinition: [
			{ name: 'RAz_Filename', label: 'Filename' },
			{ name: 'RAc_File_Type', label: 'File Type' },
			{ name: 'RAc_Status', label: 'Status' },
			// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
		],
		descriptionDefinition: ['RAz_Filename'],
	},
	BSB_CODES: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'BBz_BSB',
		resourcePath: 'bsb-codes',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'BBz_BSB', label: 'Bank State Branch' },
			{ name: 'BBz_Bank', label: 'Bank' },
			{ name: 'BBz_Suburb', label: 'Suburb' },
			// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
		],
		descriptionDefinition: ['BBz_Bank', 'BBz_Suburb'],
	},
	INSURER: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'IDz_Insurer',
		resourcePath: 'insurer',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'IDz_Insurer', label: 'Insurer' },
			{ name: 'IDz_Business_Name', label: 'Business Name' },
			{ name: 'IDz_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['IDz_Business_Name'],
	},
	ADVISER: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'BDz_Name',
		resourcePath: 'adviser-details',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'BDz_Name', label: 'Adviser' },
			{ name: 'BDz_Business_Name', label: 'Business Name' },
			{ name: 'BDz_C_Surname', label: 'Surname' },
			{ name: 'BDz_C_Given_Names', label: 'Given Names' },
			{ name: 'BDc_Deleted', label: 'Deleted', hidden: true },
		],
		// do not add functionality to filter deleted fields without exempting THz_Fee_AdviserXX
		descriptionDefinition: ['BDz_Business_Name'],
	},
	MASTER_ADVISER: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'BDz_Name',
		resourcePath: 'adviser-details/adviser-dealer',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'BDz_Name', label: 'Adviser' },
			{ name: 'BDz_Business_Name', label: 'Business Name' },
			{ name: 'BDc_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['BDz_Business_Name'],
	},
	ADVISER_AUTHORITY_ADVISER: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'BDz_Name',
		resourcePath: 'adviser-details/adviser-authority-adviser',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'BDz_Name', label: 'Adviser' },
			{ name: 'BDz_C_Surname', label: 'Surname' },
			{ name: 'BDz_C_Given_Names', label: 'Given Names' },
			{ name: 'BDc_Deleted', label: 'Deleted', hidden: true },
		],
		filters: [
			{
				name: 'AVz_Company_Code',
				value: '',
				operation: FilterType.EQUALS,
			}, // this is placeholder for filter-by
		],
		descriptionDefinition: ['BDz_C_Given_Names'],
	},
	MEMBER_ADVISER_PROPORTIONS: {
		...AdviserProportions,
		resourcePath: 'member-adviser-proportions',
	},
	EMPLOYER_ADVISER_PROPORTIONS: {
		...AdviserProportions,
		resourcePath: 'employer-adviser-proportions',
	},
	MEMBER_CORRESPONDENCE_REGISTER: {
		...CorrespondenceRegister,
		resourcePath: 'member-correspondence-register',
	},
	PAYROLL_CORRESPONDENCE_REGISTER: {
		...CorrespondenceRegister,
		resourcePath: 'payroll-correspondence-register',
	},
	ADVISER_CORRESPONDENCE_REGISTER: {
		...CorrespondenceRegister,
		resourcePath: 'adviser-correspondence-register',
	},
	// NOTE: Jake: 2019-10-30 - WEBUI-2639
	// Not 100% certain yet. But any field with its type defined as:
	// AcurityFieldTypes.Inv_Mgr. Seems to map to this.
	INVESTMENT_MANAGER: InvestmentManager,
	// NOTE: Jake: 2019-10-30 - WEBUI-2639
	// Not 100% certain yet. But any field with its type defined as:
	// AcurityFieldTypes.INVMGRL. Seems to map to this.
	INVESTMENT_MANAGER_LONG: {
		...InvestmentManager,
		rowIdKey: 'IMz_Long_Code',
		columnDefinition: [
			{ name: 'IMz_Long_Code', label: 'Investment Manager' },
			{ name: 'IMz_Name', label: 'Business Name' },
			// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
		],
	},
	NON_MODEL_INVESTMENT_MANAGER: NonModelInvestmentManager,
	MODEL_MANAGER: ModelInvestmentManager,
	CLIENT: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'D2z_Client',
		resourcePath: 'personal-details',
		dropdownWidth: 500,
		columnDefinition: [
			{ name: 'D2z_Client', label: 'Client' },
			{ name: 'D2z_Surname', label: 'Surname' },
			{ name: 'D2z_Given_Names', label: 'Given Names' },
			{ name: 'D2z_Sex', label: 'Sex', excludeFromFilter: true },
			{
				name: 'D2d_Birth',
				label: 'Date of Birth',
				excludeFromFilter: true,
			},
			// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
		],
		descriptionDefinition: ['D2z_Given_Names', 'D2z_Surname'],
	},
	MESSAGE: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'MSl_Record_No',
		resourcePath: 'message-history',
		dropdownWidth: 1200,
		columnDefinition: [
			{ name: 'MSl_Record_No', label: 'Record No' },
			{ name: 'MSz_Message_In_Out', label: 'Message Received or Sent' },
			{ name: 'MSz_Message_Type', label: 'Message Type' },
			{ name: 'MSz_ConversationID', label: 'Conversation ID' },
			{ name: 'MSz_PartID', label: 'Superstream PartID' },
			{ name: 'MSz_Status', label: 'Message Status' },
			{ name: 'MSs_Sequence', label: 'Sequence Number' },
			{
				name: 'MSk_Created',
				label: 'Creation Date',
				dataType: { name: 'Date' },
			},
			{
				name: 'MSk_Sent',
				label: 'Sent Date',
				dataType: { name: 'Date' },
			},
			{ name: 'MSc_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['MSz_ConversationID'],
	},
	MESSAGE_FOR_MEMBER: {
		...Message,
		filters: [{ name: 'fund' }, { name: 'member' }],
	},
	MESSAGE_FOR_CLIENT: {
		...Message,
		filters: [{ name: 'client' }],
	},
	CONVERSATION_ID: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'MSz_ConversationID',
		resourcePath: 'message-history',
		dropdownWidth: 500,
		columnDefinition: [
			{ name: 'MSl_Record_No', label: 'Record No' },
			{ name: 'MSz_ConversationID', label: 'Conversation ID' },
			{ name: 'MSc_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['MSz_ConversationID'],
	},
	BANK_ACCOUNT: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'BEz_Bank_Acct_No',
		resourcePath: 'bank-account',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'BEz_Bank_Account', label: 'Bank Account' },
			{ name: 'BEz_BSB', label: 'BSB' },
			{ name: 'BEz_Bank_Acct_No', label: 'Bank Account Number' },
			{ name: 'BEz_Bank_Acct_Name', label: 'Bank Account Name' },
			{ name: 'BEc_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['BEz_Bank_Acct_Name'],
	},
	GLOBAL_BANK_ACCOUNT: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'BEz_Bank_Acct_No',
		resourcePath: 'global-bank-account',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'BEz_Bank_Account', label: 'Bank Account' },
			{ name: 'BEz_BSB', label: 'BSB' },
			{ name: 'BEz_Bank_Acct_No', label: 'Bank Account Number' },
			{ name: 'BEz_Bank_Acct_Name', label: 'Bank Account Name' },
			{ name: 'BEc_Deleted', label: 'Deleted', hidden: true },
		],
		descriptionDefinition: ['BEz_Bank_Acct_Name'],
	},
	BANK_RECONCILIATION_ACCOUNT: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'ZYz_BSB_And_Bank_Acct_No',
		resourcePath: 'bank-statement-reconciliation',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'BEz_Bank_Account', label: 'Bank Account' },
			{ name: 'BEz_BSB', label: 'BSB' },
			{ name: 'BEz_Bank_Acct_No', label: 'Bank Account Number' },
			{ name: 'BEz_Bank_Acct_Name', label: 'Bank Account Name' },
			{ name: 'BEc_Deleted', label: 'Deleted', hidden: true },
			{
				name: 'ZYz_BSB_And_Bank_Acct_No',
				label: 'BSB and Bank Acct No',
				hidden: true,
			},
		],
		descriptionDefinition: ['BEz_Bank_Acct_Name'],
	},
	TRANSN_REF_NUMBER: TransactionRef,
	TRANSN_REVERSE_ADJUSTMENT_REF_NUMBER: {
		...TransactionRef,
		filters: [
			// NOTE(Albert): 2020-07-16 - WEBUI-2697
			// Where conditions for Transaction To Reverse search field retrieved
			// from Rev Adjustment or Ext. Payment C form (d_revadj~rcpt):
			// - NOT ((THz_Posted = 'C')) AND
			// - NOT ((THl_Elec_Batch_Ref <> 0)) AND
			// - NOT (THz_Transaction <> '77' or THl_Reversed_By <> 0 or THl_Reversing_Ref <> 0 )
			{
				name: 'THz_Posted',
				operation: FilterType.EQUALS,
				value: 'C',
				not: true,
				andJoin: true,
				ignoreCase: false,
			},
			{
				name: 'THl_Elec_Batch_Ref',
				operation: FilterType.EQUALS,
				value: 0,
				andJoin: true,
				ignoreCase: false,
			},
			{
				name: 'THz_Transaction',
				operation: FilterType.EQUALS,
				value: '77',
				andJoin: true,
				ignoreCase: false,
			},
			{
				name: 'THl_Reversed_By',
				operation: FilterType.EQUALS,
				value: 0,
				andJoin: true,
				ignoreCase: false,
			},
			{
				name: 'THl_Reversing_Ref',
				operation: FilterType.EQUALS,
				value: 0,
				andJoin: true,
				ignoreCase: false,
			},
		],
	},
	TABLE_DEFINITIONS: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'TRz_Code',
		resourcePath: 'table-definitions',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'TRz_Code', label: 'Code' },
			{
				name: 'TRz_Description',
				label: 'Description',
			},
		],
		descriptionDefinition: [],
	},
	PAYEE: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'PRz_Code',
		resourcePath: 'payee-details',
		dropdownWidth: 800,
		columnDefinition: [
			{ name: 'PRz_Code', label: 'Code' },
			{
				name: 'PRz_Dest_Prod_ID_No',
				label: 'Destination Product ID Number',
			},
			{ name: 'PRz_Name', label: 'Name' },
			{ name: 'PRz_Admin_Name', label: 'Product Name' },
			{ name: 'PRz_Aust_Business_No', label: 'ABN', hidden: true },
			{ name: 'PRz_ProdID', label: 'Prod ID', hidden: true },
			{ name: 'PRc_Type', label: 'Pay Type', hidden: true },
			{
				name: 'PRv_RcvElServiceAddr',
				label: 'Rcv Electronic Service Address',
				hidden: true,
			},
			{ name: 'PRc_Deleted', label: 'Deleted', hidden: true },
			{ name: 'PRc_Rollover', label: 'Rollover', hidden: true },
		],
		descriptionDefinition: ['PRz_Name'],
	},
	// NOTE(Jake): 2018-12-05 - WEBUI-1204
	// Pulled from outdated "insurance-summary", this might be able to be deleted?
	POLICY: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'PLl_Policy_Number',
		resourcePath: 'policy-details',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'PLl_Policy_Number', label: 'Policy Number' },
			{ name: 'PLd_Commencement', label: 'Commencement' },
			{ name: 'PLz_Status', label: 'Status' },
			// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
		],
		descriptionDefinition: ['PLz_Status'],
	},
	REPORT: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'ReportCode',
		resourcePath: 'report-codes',
		dropdownWidth: 500,
		columnDefinition: [
			{ name: 'ReportCode', label: 'Report Code' },
			{ name: 'Report', label: 'Report' },
			{ name: 'ReportGroup', label: 'Report Group' },
			// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
		],
		// NOTE (York): Maybe only `Report` is needed
		descriptionDefinition: ['Report', 'ReportGroup'],
	},
	QAS: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'picklistWithPostcode',
		resourcePath: 'qas',
		dropdownWidth: 500,
		columnDefinition: [
			{ name: 'picklistWithPostcode', label: 'Address' },
			// { name: 'HasNoSoftDeleteField', label: 'Deleted', hidden: true},
		],
		descriptionDefinition: ['picklistWithPostcode'],
	},

	GROUP_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'group-investment-profile/inv-mgr-list',
	},
	EMPLOYER_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'employer-investment-profile/inv-mgr-list',
	},
	ADVISER_MODEL_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'adviser-model-investment-profile/inv-mgr-list',
	},
	CATEGORY_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'category-investment-profile/inv-mgr-list',
	},
	FUND_MODEL_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'fund-model-investment-profile/inv-mgr-list',
	},
	FUND_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'fund-investment-profile/inv-mgr-list',
	},
	CONT_TAX_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'cont-tax-investment-profile/inv-mgr-list',
	},
	PENSION_REDEMPTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'pension-redemption-investment-profile/inv-mgr-list',
	},
	CONTRIBUTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'contribution-investment-profile/inv-mgr-list',
	},
	REDEMPTION_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'redemption-investment-profile/inv-mgr-list',
	},
	USER_DEFINED_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'user-defined-investment-profile/inv-mgr-list',
	},
	MEMBER_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'member-investment-profile/inv-mgr-list',
	},
	MEMBER_INVESTMENT_PROFILE_2_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'member-investment-profile-2/inv-mgr-list',
	},
	ROLLOVER_REQUEST_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'rollover-request-investment-profile/inv-mgr-list',
	},
	TRANSFER_INVESTMENT_PROFILE_INVESTMENT_MANAGER: {
		...InvestmentProfileInvestmentManager,
		resourcePath: 'transfer-investment-profile/inv-mgr-list',
	},

	BANK_ACCOUNT_PAYMENT_PROPORTIONS_BANK_ACCOUNT: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'BEz_Bank_Account',
		resourcePath: 'bank-account-payment-proportions/inv-mgr-list',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'BEz_Bank_Account', label: 'Code' },
			{ name: 'BEz_BSB', label: 'BSB' },
			{ name: 'BEz_Bank_Acct_No', label: 'Bank Account Number' },
		],
		descriptionDefinition: ['BEz_Bank_Acct_Name'],
		filters: [
			{ name: 'IPz_Fund' },
			{ name: 'IPz_Member_No' },
			{ name: 'IPz_Client' },
			{ name: 'IPd_Effective' },
			{ name: 'IPz_Transfer_Fund' },
			{ name: 'IPz_Transfer_Cat' },
		],
	},

	USER: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'USi_Identity',
		resourcePath: 'user-details',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'USi_Identity', label: 'User ID' },
			{ name: 'USz_LoginName', label: 'Login Name' },
		],
		descriptionDefinition: ['USz_LoginName'],
	},
	RECORD_AUTHORISATION_TABLE_NAME: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'AUv_Name',
		resourcePath: 'record-authorisation-table-names',
		dropdownWidth: 400,
		columnDefinition: [{ name: 'AUv_Name', label: 'Table Name' }],
		descriptionDefinition: [],
	},
	CONTRIBUTION_LINES: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'FCz_Supplied_Trn_Ref',
		resourcePath: 'contribution-lines',
		dropdownWidth: 600,
		columnDefinition: [
			{ name: 'FCz_Supplied_Trn_Ref', label: 'Cont Ref Number' },
			{ name: 'FCl_File_Reg_Ref', label: 'File Reg Ref' },
			{ name: 'FCz_Status', label: 'Status' },
			{ name: 'FCl_Line_No', label: 'Line No' },
			{ name: 'FCs_Sequence', label: 'Sequence No' },
		],
		descriptionDefinition: [],
		filters: [{ name: 'BSl_Batch_Reference', paramName: 'receipt-ref' }],
	},
	// This should only be used by the Status Search component on the Advanced Entity Search form. Status field
	// everywhere else should be a Select component that populates its options from the codeList of the fieldModel.
	STATUS: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'CDz_Description_Code',
		resourcePath: 'member-status',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'CDz_Description_Code', label: 'Code' },
			{ name: 'CDz_Description', label: 'Description' },
		],
		descriptionDefinition: ['CDz_Description'],
	},
	TREATY_NUMBER: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'TYl_Treaty_Number',
		resourcePath: 'treaty-configuration',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'TYl_Treaty_Number', label: 'Treaty Number' },
			{ name: 'TYz_Policy_Group', label: 'Policy Group' },
			{ name: 'TYz_Reinsurer', label: 'Reinsurer' },
		],
		descriptionDefinition: [],
	},
	MESSAGE_CODE: {
		...MessageCode,
	},
	MESSAGE_CODE_SUPER_STREAM: {
		...MessageCode,
		resourcePath: 'message-code/super-stream',
	},
	BANK_STMT_REF: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'BNl_Bank_Ref',
		resourcePath: 'bank-reconciliation-history',
		dropdownWidth: 600,
		columnDefinition: [
			{ name: 'BNl_Bank_Ref', label: 'Bank Reference Id' },
			{
				name: 'BNk_Created',
				label: 'Created',
				dataType: { name: 'Date' },
			},
			{ name: 'BNz_Biller_Code', label: 'Biller Code' },
			{ name: 'BNz_CRN', label: 'Customer Reference Number' },
			{ name: 'BNf_Settlement', label: 'Settlement Amount' },
		],
		descriptionDefinition: [],
	},
	ORIG_TRANS_REF: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'FCz_Supplied_Trn_Ref',
		resourcePath: 'contribution-lines',
		dropdownWidth: 800,
		columnDefinition: [
			{ name: 'FCz_Supplied_Trn_Ref', label: 'Cont Ref Number' },
			{ name: 'FCz_Fund', label: 'Fund' },
			{ name: 'FCz_Member', label: 'Member' },
			{
				name: 'FCf_Amount',
				label: 'Amount',
				dataType: { name: 'Currency' },
			},
			{ name: 'FCl_Receipt_Ref', label: 'Receipt Ref Number' },
			{
				name: 'FCn_US_ID_Modified',
				label: 'Modified',
				render: (suggestion) =>
					combineModifiedFields(
						suggestion[ContributionLinesField.FCn_US_ID_Modified],
						suggestion[ContributionLinesField.FCk_Modified],
					),
			},
		],
		descriptionDefinition: [],
	},
	BENEFITS_PAYMENTS_DISPERSALS_CONVERSATION_ID: {
		searchType: SearchDefinitionType.DATATABLE,
		rowIdKey: 'MSz_ConversationID',
		resourcePath: 'message-history/benefits-payments-dispersals-list',
		dropdownWidth: 500,
		columnDefinition: [
			{
				name: 'MSl_Record_No',
				label: 'Record No',
				excludeFromFilter: true,
			},
			{ name: 'MSz_ConversationID', label: 'Conversation ID' },
			{
				name: 'MSc_Deleted',
				label: 'Deleted',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'FQz_External_ABN',
				label: 'ABN',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'FQz_External_ProdId',
				label: 'USI',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'FQz_External_OrgName',
				label: 'Fund',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'FQz_ExternalMemberID',
				label: 'Member ID',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'MSz_PayeeBSB',
				label: 'Payee BSB',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'MSz_PayeeBankActNo',
				label: 'Payee Bank Account No',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'MSz_PayeeBankActName',
				label: 'Payee Bank Account Name',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'MSz_OrgName',
				label: 'Org Name',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'PRz_Code',
				label: 'Payee Code',
				hidden: true,
				excludeFromFilter: true,
			},
			{
				name: 'PRc_Rollover',
				label: 'Payee Rollover',
				hidden: true,
				excludeFromFilter: true,
			},
		],
		descriptionDefinition: [],
		filters: [
			{
				name: 'FQz_Fund',
				value: '',
				operation: FilterType.EQUALS,
			}, // placeholder for filterBy
			{
				name: 'FQz_Member',
				value: '',
				operation: FilterType.EQUALS,
			}, // placeholder for filterBy
		],
	},
	CORPORATE_ACTION_ID: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'CNl_CorpActionId',
		resourcePath: 'corporate-action-elections',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'CNl_CorpActionId', label: 'Corporate Action Id' },
			{ name: 'CNz_InvMgr', label: 'Inv. Mgr. Code' },
			{ name: 'IMz_Name', label: 'Inv. Mgr. Description' },
			{ name: 'CNz_Status', label: 'Status' },
		],
		descriptionDefinition: [],
		filters: [],
	},
	SUPPRESSION_ISSUES: {
		searchType: SearchDefinitionType.SEARCH,
		rowIdKey: 'SIi_Identity',
		resourcePath: 'suppression-issues',
		dropdownWidth: 400,
		columnDefinition: [
			{ name: 'SIi_Identity', label: 'ID' },
			{ name: 'SIz_Description', label: 'Description' },
			{ name: 'SIz_Ticket', label: 'Ticket' },
		],
		descriptionDefinition: ['SIz_Description'],
	},
};

export interface SearchFieldModel extends FieldModel {
	filterBy?: string[] | FilterBy[];
	additionalFilters?: SearchFilter[];
	searchType?: SearchType;
	allowUserInput?: boolean; // true if we want to allow values typed in by users in addition to search results
	displayResultsOnFocus?: boolean;
	keepValueWhenFilterByChanges?: boolean;
	fieldTypes?: readonly FieldNameParamType[];
}

export type SearchFieldModelMap<T extends string = string> = {
	[key in Extract<T, string>]: SearchFieldModel;
};
